import { useTranslation } from 'react-i18next';
import { Button, Flex, Image, Segmented, Typography } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import { SelectedScheduleMap } from '../../../../types/MachineDetailsRobotSchedule.types';
import { SUCTION_MODE_OPTIONS } from '../../../../types/RobotScheduleMap.types';
import { DEFAULT_CLEANING_DURATION_MINUTES } from '../../../../utils/constants';
import { CleaningZones } from './components/CleaningZones/CleaningZones';
import {
  CleaningScheduleCardContainer,
  CleaningScheduleCardContent,
  CleaningScheduleCardDescription,
  CleaningScheduleCardTitle,
  CleaningScheduleCardFormItem,
} from './CleaningScheduleCard.styles';
import { CleaningSuctionEnum } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import mapPlaceHolder from 'lib/assets/images/Routine_Example.png';
import { ISvgIconName } from 'lib/components/SvgIcon/interfaces/SvgIconNames';

interface CleaningScheduleCardCV50Props {
  selectedScheduleMap: SelectedScheduleMap;
  onEdit: () => void;
}

export const CleaningScheduleCardCV50 = ({
  selectedScheduleMap: { map },
  onEdit,
}: CleaningScheduleCardCV50Props): JSX.Element => {
  const { t } = useTranslation();
  const { isReadOnly } = useMachineDetailsRobotSchedule();

  const mapImage = map.thumbnail ?? map.preview ?? mapPlaceHolder;

  // Overlay outlines are positioned from map.width/map.height, so they need map.image itself —
  // the thumbnail/preview aren't guaranteed to match those dimensions.
  const mapOverlayImage = map.image ?? map.preview ?? map.thumbnail ?? mapPlaceHolder;

  return (
    <CleaningScheduleCardContainer>
      <Flex align="center" gap={kccThemeConstants.SPACING.SM} style={{ marginBottom: kccThemeConstants.SPACING.BASE }}>
        <Image width={48} height={48} src={mapImage} fallback={mapPlaceHolder} alt={map.name} preview={false} />

        <CleaningScheduleCardContent vertical gap={2}>
          <Flex align="center" justify="space-between">
            <CleaningScheduleCardTitle>{map.name}</CleaningScheduleCardTitle>

            {!isReadOnly && (
              <Button
                type="link"
                aria-label={t('common.edit')}
                icon={<SvgIcon name="editNew" />}
                onClick={onEdit}
                style={{ width: kccThemeConstants.SIZE.S250, height: kccThemeConstants.SIZE.S250 }}
              />
            )}
          </Flex>

          {/* TODO: remove hardcode when Map supports duration */}
          <CleaningScheduleCardDescription>
            {t('createSchedule.durationInMinutes', { duration: DEFAULT_CLEANING_DURATION_MINUTES })}
          </CleaningScheduleCardDescription>
        </CleaningScheduleCardContent>
      </Flex>

      <CleaningZones
        mapImageSrc={mapOverlayImage}
        mapName={map.name}
        mapWork={map.jsonData?.jsonContent?.work}
        mapWorkUrl={map.work}
        mapConfig={map.jsonData?.jsonContent?.config}
        mapFunction={map.jsonData?.jsonContent?.function}
        mapFunctionUrl={map.function}
        mapWidthPx={map.width}
        mapHeightPx={map.height}
      />

      <Flex
        vertical
        align="flex-start"
        gap={kccThemeConstants.SPACING.XS}
        style={{ marginTop: kccThemeConstants.SPACING.BASE }}
      >
        <Typography.Text
          style={{
            fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
            lineHeight: kccThemeConstants.TYPOGRAPHY.LINE_HEIGHT.SM,
          }}
        >
          {t('common.suctionMode')}
        </Typography.Text>

        <CleaningScheduleCardFormItem name="suctionMode">
          <Segmented<CleaningSuctionEnum>
            disabled={isReadOnly}
            options={SUCTION_MODE_OPTIONS.map(option => ({
              label: t(option.label),
              title: t(option.label),
              value: option.value,
              icon: <SvgIcon name={option.icon as ISvgIconName} />,
            }))}
          />
        </CleaningScheduleCardFormItem>
      </Flex>
    </CleaningScheduleCardContainer>
  );
};
